/*
 * One way to print every document (invoices, sales, purchase, stock transfer).
 *
 * The backend returns { html, print, copies, preview }. Browsers can't tell a
 * printer the paper size, orientation or number of copies, which is why thermal
 * receipts came out with blank space or sideways. So this:
 *   1. reads the paper size / orientation from the format (Invoice Builder
 *      formats carry it inside the HTML),
 *   2. renders the page off-screen and, for thermal rolls, measures its real
 *      length,
 *   3. picks the printer from Settings > Printer Setup,
 *   4. checks for mismatches (no printer, format wider than the roll...),
 *   5. prints straight to the printer with exact size, orientation and copies
 *      (desktop app, or QZ Tray in the browser), or shows a preview first when
 *      Print Preferences asks for one.
 * The browser print dialog is only the last fallback.
 */
import qz from "qz-tray";
import { ZP } from "../../settings/system-settings/invoice-builder/compile";
import { connectQz, listPrinters, PRINT_STATE } from "../tsc-label-printer";
import { getPrintSettings, printerFor, receiptProfile } from "./print-settings";
import { openPrintDialog } from "./print-dialog";
import { buildEscposReceipt, buildLineTest } from "./escpos";
import successToast from "../success-toast";
import errorToast from "../error-toast";

const PX_PER_MM = 96 / 25.4;
const A4 = { width: 210, height: 297 };

/* ---------- 1. what is this print? ---------- */

function decodeBase64Utf8(b64) {
  return decodeURIComponent(escape(window.atob(b64)));
}

// Paper of the format. Builder pages carry their template; older formats are A4.
export function analyzePrintHtml(html) {
  const isBuilder = /<meta name="zp-builder"/.test(html || "");
  let template = null;
  if (isBuilder) {
    const m = /window\.__ZP_TPL__="([A-Za-z0-9+/=]+)"/.exec(html);
    try {
      template = m ? JSON.parse(decodeBase64Utf8(m[1])) : null;
    } catch {
      template = null;
    }
  }
  if (template) {
    const ps = ZP.paperSize(template);
    const landscape = !ps.continuous && template.paper?.orientation === "landscape";
    return {
      isBuilder: true,
      name: template.name || "",
      receipt: ps.continuous,
      landscape,
      layoutWidth: ps.width, // as laid out (landscape already swapped)
      layoutHeight: ps.height, // 0 for rolls until measured
      contentWidth: ps.contentWidth,
      margins: ps.margins,
      preset: template.paper?.preset || "",
    };
  }
  return {
    isBuilder,
    name: "",
    receipt: false,
    landscape: false,
    layoutWidth: A4.width,
    layoutHeight: A4.height,
    contentWidth: A4.width - 16,
    margins: { top: 8, right: 8, bottom: 8, left: 8 },
    preset: "A4",
  };
}

/* ---------- 2. render off-screen and build print-ready HTML ---------- */

function waitForImages(doc, timeoutMs) {
  const imgs = Array.from(doc.images || []).filter((img) => !img.complete);
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(imgs.map((img) => new Promise((r) => { img.onload = img.onerror = r; }))),
    new Promise((r) => setTimeout(r, timeoutMs)),
  ]);
}

async function renderOffscreen(html, widthMm) {
  const frame = document.createElement("iframe");
  Object.assign(frame.style, { position: "fixed", left: "-10000px", top: "0", width: Math.ceil(widthMm * PX_PER_MM) + 40 + "px", height: "200px", border: "0", visibility: "hidden" });
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  await new Promise((resolve) => {
    frame.onload = resolve;
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(resolve, 1500); // some browsers skip onload for document.write
  });
  await waitForImages(doc, 2500);
  await new Promise((r) => setTimeout(r, 30));
  return { frame, doc };
}

const mm = (n) => Math.round(n * 100) / 100 + "mm";

// Static HTML (no scripts) sized exactly for the paper.
async function preparePrint(html, info) {
  const { frame, doc } = await renderOffscreen(html, info.layoutWidth);
  try {
    if (!info.isBuilder) {
      doc.querySelectorAll("script").forEach((s) => s.remove());
      return { html: "<!DOCTYPE html>" + doc.documentElement.outerHTML, widthMm: A4.width, heightMm: A4.height };
    }
    const mount = doc.getElementById("zp-mount");
    const root = doc.querySelector(".zp-root");
    if (!mount || !root) throw new Error("The print page did not render (no invoice data?)");
    const styles = Array.from(doc.querySelectorAll("style")).map((s) => s.textContent).join("\n");
    const m = info.margins;
    const W = info.layoutWidth;
    let H = info.layoutHeight;
    let pageCss;
    if (info.receipt) {
      // one continuous page as long as the content; margins become padding
      H = Math.ceil(root.getBoundingClientRect().height / PX_PER_MM + m.top + m.bottom + 1);
      pageCss = `@page{size:${mm(W)} ${mm(H)};margin:0}.zp-sheet{padding:${mm(m.top)} ${mm(m.right)} ${mm(m.bottom)} ${mm(m.left)}!important}`;
    } else {
      // real pages: margins repeat on every page
      pageCss = `@page{size:${mm(W)} ${mm(H)};margin:${mm(m.top)} ${mm(m.right)} ${mm(m.bottom)} ${mm(m.left)}}.zp-sheet{padding:0!important}`;
    }
    const out =
      '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' + styles + "</style><style>" +
      pageCss +
      "html,body{margin:0!important;padding:0!important;background:#fff!important}" +
      ".zp-sheet{width:auto!important;min-height:0!important;margin:0!important;box-shadow:none!important;background:#fff!important;box-sizing:border-box}" +
      "</style></head><body>" + mount.innerHTML + "</body></html>";
    return { html: out, widthMm: W, heightMm: H };
  } finally {
    frame.remove();
  }
}

/* ---------- 3. how can we reach a printer? ---------- */

const hasDesktopHtmlPrint = () => typeof window.electron?.printHtmlJob === "function";
const hasOldDesktopPrint = () => !!window.electron?.ipcRenderer && !hasDesktopHtmlPrint();

async function pickTransport() {
  if (hasDesktopHtmlPrint()) {
    let printers = [];
    try {
      printers = (await window.electron.listPrinters()) || [];
    } catch {}
    return { kind: "desktop", printers };
  }
  if (hasOldDesktopPrint()) return { kind: "desktop-legacy", printers: [] };
  const state = await connectQz();
  if (state === PRINT_STATE.OPEN) {
    const { printers } = await listPrinters();
    return { kind: "qz", printers };
  }
  return { kind: "browser", printers: [], qzState: state };
}

/* ---------- 4. mismatches ---------- */

export function findIssues({ info, job, transport, printer, settings, copies, isTest }) {
  const issues = [];
  const add = (level, text) => issues.push({ level, text });
  if (transport.kind === "browser") {
    add(
      "warn",
      `QZ Tray is not running on this computer, so the browser print dialog is used. It can't set the paper size, orientation or copies${
        info.receipt ? " — thermal receipts may print with blank space or sideways" : ""
      }. Start QZ Tray (Settings → Printer Setup) to print directly.`
    );
    if (copies > 1 && info.receipt) add("warn", `Only 1 copy can be printed this way; ${copies} copies are set in Print Preferences.`);
  }
  if (transport.kind === "desktop-legacy") add("info", "This desktop app version prints without choosing a printer or copies. Update the app for exact printing.");
  if (transport.kind === "qz" || transport.kind === "desktop") {
    if (!printer) add("warn", `No ${info.receipt ? "receipt" : "document"} printer is chosen in Settings → Printer Setup, so the computer's default printer is used.`);
    else if (transport.printers.length && !transport.printers.includes(printer)) add("warn", `The printer "${printer}" is not installed on this computer.`);
  }
  if (info.receipt && !isTest) {
    const profile = receiptProfile(settings, printer);
    const roll = Number(profile.rollWidth) || 80;
    const printable = Number(profile.printableWidth) || roll - 8;
    const printedWidth = Math.round((job.widthMm - info.margins.left - info.margins.right) * 10) / 10;
    if (usesEscpos(info, transport, profile)) {
      // sent as an image: a wider design is shrunk to the printable width
      if (printedWidth > printable + 0.5)
        add(
          profile.fit === "actual" ? "warn" : "info",
          profile.fit === "actual"
            ? `This format is ${printedWidth} mm wide but this printer prints ${printable} mm — it keeps its font sizes, so about ${Math.round((printedWidth - printable) / 2)} mm is cut off each side.`
            : `This format is ${printedWidth} mm wide; it is shrunk to fit this printer's ${printable} mm, so the text prints smaller than designed. Make the format ${printable} mm wide inside its margins for exact font sizes.`
        );
    } else {
      if (job.widthMm > roll + 0.5) add("warn", `This format is ${job.widthMm} mm wide but the receipt roll is ${roll} mm — it will be cut off.`);
      if (printedWidth > printable + 0.5)
        add(
          "warn",
          `The format prints ${printedWidth} mm wide but this printer can only print ${printable} mm — the edges will be cut off. In the Invoice Builder set the left and right margins to at least ${Math.ceil((job.widthMm - printable) / 2)} mm.`
        );
    }
    if (printer && printer === settings.documentPrinter && printer !== settings.receiptPrinter) add("warn", `A thermal receipt format is going to the document printer "${printer}".`);
  } else if (printer && printer === settings.receiptPrinter && printer !== settings.documentPrinter) {
    add("warn", `A ${info.preset || "page"} format is going to the receipt printer "${printer}".`);
  }
  return issues;
}

/* ---------- 5. send it ---------- */

function physicalSize(info, job) {
  // printers want the sheet in portrait; orientation rotates it
  if (info.landscape) return { width: Math.min(job.widthMm, job.heightMm), height: Math.max(job.widthMm, job.heightMm) };
  return { width: job.widthMm, height: job.heightMm };
}

function repeatPages(html, copies) {
  if (copies <= 1) return html;
  return html.replace(/<body>([\s\S]*)<\/body>/, (all, body) => "<body>" + Array.from({ length: copies }, () => `<div style="break-after:page;page-break-after:always">${body}</div>`).join("") + "</body>");
}

function browserPrint(html) {
  return new Promise((resolve) => {
    const frame = document.createElement("iframe");
    Object.assign(frame.style, { position: "absolute", width: "0", height: "0", border: "0" });
    document.body.appendChild(frame);
    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      frame.remove();
      resolve();
    }, 300);
  });
}

// Raw ESC/POS is used for thermal receipts when the printer's profile asks
// for it and the desktop app or QZ Tray can send raw bytes.
function usesEscpos(info, transport, profile) {
  if (!info.receipt || profile.mode === "driver") return false;
  return transport.kind === "qz" || (transport.kind === "desktop" && typeof window.electron?.printRaw === "function");
}

// Thermal receipts go as raw ESC/POS (an image in the printer's own language),
// so no driver is involved. Returns false when that route isn't available.
async function sendEscpos({ info, job, transport, printer, copies, settings, profileOverride }) {
  const profile = { ...receiptProfile(settings, printer), ...(profileOverride || {}) };
  if (!usesEscpos(info, transport, profile)) return false;
  const name = printer || (transport.kind === "qz" ? await qz.printers.getDefault() : "");
  if (!name) throw new Error("Choose the receipt printer in Settings → Printer Setup");
  const { frame, doc } = await renderOffscreen(job.html, job.widthMm);
  let bytes;
  try {
    bytes = await buildEscposReceipt(doc, info, profile, copies);
  } finally {
    frame.remove();
  }
  await sendRaw(transport, name, bytes);
  return true;
}

function sendRaw(transport, name, bytes) {
  if (transport.kind === "desktop") return window.electron.printRaw(name, bytes);
  return qz.print(qz.configs.create(name), [{ type: "raw", format: "command", flavor: "base64", data: window.btoa(bytes) }]);
}

// Printer Setup's line test: samples printed with every image setting.
export async function printLineTest(printer) {
  try {
    const transport = await pickTransport();
    if (!(transport.kind === "qz" || (transport.kind === "desktop" && typeof window.electron?.printRaw === "function")))
      throw new Error("Needs QZ Tray or the desktop app (the browser can't send printer commands)");
    const name = printer || (transport.kind === "qz" ? await qz.printers.getDefault() : "");
    if (!name) throw new Error("Choose the receipt printer first");
    await sendRaw(transport, name, buildLineTest(receiptProfile(getPrintSettings(), printer)));
    successToast(`Line test sent to ${name}`);
  } catch (e) {
    errorToast(`Line test failed: ${e?.message || e}`);
  }
}

async function send(ctx) {
  if (await sendEscpos(ctx)) return;
  const { info, job, transport, printer, copies } = ctx;
  const size = physicalSize(info, job);
  const m = info.margins;
  if (transport.kind === "desktop") {
    await window.electron.printHtmlJob({
      html: job.html,
      deviceName: printer || "",
      copies,
      landscape: info.landscape,
      widthMm: size.width,
      heightMm: size.height,
      marginsMm: info.receipt ? null : m,
    });
    return;
  }
  if (transport.kind === "desktop-legacy") {
    window.electron.ipcRenderer.send("print-html", job.html, { widthMm: size.width, heightMm: size.height });
    return;
  }
  if (transport.kind === "qz") {
    const name = printer || (await qz.printers.getDefault());
    const config = qz.configs.create(name, {
      size: { width: size.width, height: size.height },
      units: "mm",
      orientation: info.landscape ? "landscape" : "portrait",
      copies,
      margins: info.receipt ? 0 : { top: m.top, right: m.right, bottom: m.bottom, left: m.left },
      scaleContent: false,
      rasterize: true,
      colorType: info.receipt ? "blackwhite" : "color",
    });
    const pageWidth = info.receipt ? job.widthMm : job.widthMm - m.left - m.right;
    await qz.print(config, [{ type: "pixel", format: "html", flavor: "plain", data: job.html, options: { pageWidth } }]);
    return;
  }
  await browserPrint(info.receipt ? job.html : repeatPages(job.html, copies));
}

/* ---------- the one function every Print button calls ---------- */

// block: one entry of the backend's print response { html, print, copies, preview }
// options.printer forces a printer and options.isTest skips the width checks
// (both used by the ruler test print in Printer Setup).
// options.profileOverride replaces receipt profile values for this print only.
export async function printDocument(block, { docType, printer: forcedPrinter, isTest = false, profileOverride = null } = {}) {
  if (!block || typeof block.html !== "string" || !block.html.trim()) {
    errorToast("Nothing to print: the server sent no print page");
    return false;
  }
  if (block.print === false) {
    errorToast("Printing is turned off for this document in Print Preferences");
    return false;
  }
  const settings = getPrintSettings();
  const copies = Math.max(1, Math.min(100, parseInt(block.copies, 10) || 1));
  const info = analyzePrintHtml(block.html);
  try {
    const [job, transport] = await Promise.all([preparePrint(block.html, info), pickTransport()]);
    if (transport.kind === "browser" && !settings.browserFallback) {
      errorToast("No printer connection: start QZ Tray or enable the browser fallback in Settings → Printer Setup");
      return false;
    }
    const printer = forcedPrinter || printerFor(settings, docType, info.receipt);
    const issues = findIssues({ info, job, transport, printer, settings, copies, isTest });
    const warn = issues.some((i) => i.level === "warn");
    const ctx = { info, job, transport, printer, copies, settings, profileOverride };
    if (block.preview || (warn && settings.warnBeforePrint)) {
      const raw = usesEscpos(info, transport, receiptProfile(settings, printer));
      const go = await openPrintDialog({ ...ctx, issues, raw, mode: block.preview ? "preview" : "warning" });
      if (!go) return false;
    }
    await send(ctx);
    if (transport.kind !== "browser") successToast(`Sent ${copies > 1 ? copies + " copies " : ""}to ${printer || "the default printer"}`);
    return true;
  } catch (e) {
    console.error("Print failed", e);
    errorToast(`Print failed: ${e?.message || e}`);
    return false;
  }
}
